import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  ShieldAlert,
  Trash2,
  Move,
  Pencil,
  HandCoins,
  Euro,
  Printer,
  Ban,
  FileBarChart2,
  LogIn,
  Play,
  Square,
  Settings as SettingsIcon,
  RotateCcw,
  Search,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

// Map action → icon + accent. Keeps the UI scannable: a glance at the
// colour stripe shows what kind of event happened.
const ACTION_META = {
  "table.delete":          { Icon: Trash2,        tone: "bg-red-100 text-red-700 border-red-300" },
  "table.transfer":        { Icon: Move,          tone: "bg-amber-100 text-amber-800 border-amber-300" },
  "table.correction":      { Icon: Pencil,        tone: "bg-orange-100 text-orange-800 border-orange-300" },
  "table.partial-payment": { Icon: HandCoins,     tone: "bg-violet-100 text-violet-800 border-violet-300" },
  "table.close":           { Icon: Euro,          tone: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  "table.clear-session":   { Icon: RotateCcw,     tone: "bg-slate-100 text-slate-700 border-slate-300" },
  "receipt.cancel":        { Icon: Ban,           tone: "bg-red-100 text-red-700 border-red-300" },
  "receipt.reprint":       { Icon: Printer,       tone: "bg-blue-100 text-blue-700 border-blue-300" },
  "receipt.issue":         { Icon: Euro,          tone: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  "report.zreport":        { Icon: FileBarChart2, tone: "bg-rose-100 text-rose-800 border-rose-300" },
  "report.xreport":        { Icon: FileBarChart2, tone: "bg-sky-100 text-sky-700 border-sky-300" },
  "shift.open":            { Icon: Play,          tone: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  "shift.close":           { Icon: Square,        tone: "bg-amber-100 text-amber-800 border-amber-300" },
  "auth.login":            { Icon: LogIn,         tone: "bg-slate-100 text-slate-700 border-slate-300" },
  "settings.update":       { Icon: SettingsIcon,  tone: "bg-yellow-100 text-yellow-800 border-yellow-300" },
};

const SEVERITY_BADGE = {
  info:     "bg-slate-100 text-slate-600 border-slate-300",
  warn:     "bg-amber-100 text-amber-800 border-amber-300",
  critical: "bg-red-100 text-red-800 border-red-300 font-bold",
};

const PAGE_SIZE = 50;

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  } catch {
    return iso;
  }
}

// Convert YYYY-MM-DD (local) → ISO8601 midnight UTC for the date filter.
function dateInputToIso(s, end = false) {
  if (!s) return "";
  // Use local timezone: append the time-of-day then convert to ISO.
  const d = new Date(s + (end ? "T23:59:59" : "T00:00:00"));
  return d.toISOString();
}

export default function AuditFeedPanel() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(false);

  // Filter state ------------------------------------------------
  const [actions, setActions] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterAction, setFilterAction] = useState("all");
  const [filterUser, setFilterUser] = useState("all");
  const [filterTarget, setFilterTarget] = useState("all");
  const [filterSeverity, setFilterSeverity] = useState("all");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterSearch, setFilterSearch] = useState("");
  // Inline search bumps a "version" so the debounced effect refetches.
  const [searchVer, setSearchVer] = useState(0);

  // Retention controls -----------------------------------------
  const [retention, setRetention] = useState(0); // 0 = κρατά για πάντα
  const [retentionDirty, setRetentionDirty] = useState(false);

  const loadRetention = async () => {
    try {
      const r = await http.get("/settings");
      setRetention(Number(r.data?.audit_retention_days ?? 0));
      setRetentionDirty(false);
    } catch {
      /* noop */
    }
  };
  useEffect(() => { loadRetention(); }, []);

  const saveRetention = async () => {
    try {
      const r = await http.get("/settings");
      const next = { ...r.data, audit_retention_days: Number(retention) || 0 };
      await http.put("/settings", next);
      toast.success(retention > 0
        ? `Audit retention: ${retention} ημέρες`
        : "Audit retention: ποτέ (κρατά πάντα)");
      setRetentionDirty(false);
    } catch (e) {
      toast.error("Αποτυχία αποθήκευσης retention");
    }
  };

  // Load filter dictionaries (actions + users) once.
  useEffect(() => {
    (async () => {
      try {
        const [a, u] = await Promise.all([
          http.get("/audit/actions"),
          http.get("/users").catch(() => ({ data: [] })),
        ]);
        setActions(a.data?.actions || []);
        setUsers(u.data || []);
      } catch {
        /* non-fatal */
      }
    })();
  }, []);

  // Debounce the search box so typing doesn't hit the server on every key.
  useEffect(() => {
    const t = setTimeout(() => setSearchVer((v) => v + 1), 350);
    return () => clearTimeout(t);
  }, [filterSearch]);

  const queryParams = useMemo(() => {
    const p = new URLSearchParams();
    p.set("limit", String(PAGE_SIZE));
    p.set("skip", String(page * PAGE_SIZE));
    if (filterAction !== "all") p.set("action", filterAction);
    if (filterUser !== "all") p.set("user_id", filterUser);
    if (filterTarget !== "all") p.set("target_type", filterTarget);
    if (filterSeverity !== "all") p.set("severity", filterSeverity);
    if (filterFrom) p.set("date_from", dateInputToIso(filterFrom, false));
    if (filterTo) p.set("date_to", dateInputToIso(filterTo, true));
    if (filterSearch.trim()) p.set("search", filterSearch.trim());
    return p.toString();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterAction, filterUser, filterTarget, filterSeverity, filterFrom, filterTo, searchVer]);

  // Refetch when filters or page change.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBusy(true);
      try {
        const r = await http.get(`/audit?${queryParams}`);
        if (!cancelled) {
          setItems(r.data?.items || []);
          setTotal(r.data?.total || 0);
        }
      } catch (e) {
        if (!cancelled) {
          toast.error("Σφάλμα φόρτωσης audit feed");
          setItems([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [queryParams]);

  const resetFilters = () => {
    setFilterAction("all");
    setFilterUser("all");
    setFilterTarget("all");
    setFilterSeverity("all");
    setFilterFrom("");
    setFilterTo("");
    setFilterSearch("");
    setPage(0);
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-4" data-testid="audit-feed-panel">
      {/* Filter bar -------------------------------------------------- */}
      <div className="bg-white border-2 border-slate-200 rounded-lg p-3 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs font-bold text-slate-600 mb-1 block">Ενέργεια</Label>
            <Select value={filterAction} onValueChange={(v) => { setFilterAction(v); setPage(0); }}>
              <SelectTrigger data-testid="audit-filter-action"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Όλες οι ενέργειες</SelectItem>
                {actions.map((a) => (
                  <SelectItem key={a.code} value={a.code}>{a.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-bold text-slate-600 mb-1 block">Χρήστης</Label>
            <Select value={filterUser} onValueChange={(v) => { setFilterUser(v); setPage(0); }}>
              <SelectTrigger data-testid="audit-filter-user"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Όλοι οι χρήστες</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-bold text-slate-600 mb-1 block">Τύπος Στόχου</Label>
            <Select value={filterTarget} onValueChange={(v) => { setFilterTarget(v); setPage(0); }}>
              <SelectTrigger data-testid="audit-filter-target"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Όλα</SelectItem>
                <SelectItem value="table">Τραπέζι</SelectItem>
                <SelectItem value="receipt">Απόδειξη</SelectItem>
                <SelectItem value="shift">Βάρδια</SelectItem>
                <SelectItem value="report">Αναφορά</SelectItem>
                <SelectItem value="user">Χρήστης</SelectItem>
                <SelectItem value="settings">Ρυθμίσεις</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-bold text-slate-600 mb-1 block">Σοβαρότητα</Label>
            <Select value={filterSeverity} onValueChange={(v) => { setFilterSeverity(v); setPage(0); }}>
              <SelectTrigger data-testid="audit-filter-severity"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Όλες</SelectItem>
                <SelectItem value="info">Πληροφορία</SelectItem>
                <SelectItem value="warn">Προσοχή</SelectItem>
                <SelectItem value="critical">Κρίσιμο</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-xs font-bold text-slate-600 mb-1 block">Από</Label>
            <Input
              type="date"
              value={filterFrom}
              onChange={(e) => { setFilterFrom(e.target.value); setPage(0); }}
              data-testid="audit-filter-from"
            />
          </div>
          <div>
            <Label className="text-xs font-bold text-slate-600 mb-1 block">Έως</Label>
            <Input
              type="date"
              value={filterTo}
              onChange={(e) => { setFilterTo(e.target.value); setPage(0); }}
              data-testid="audit-filter-to"
            />
          </div>
          <div className="lg:col-span-1">
            <Label className="text-xs font-bold text-slate-600 mb-1 block">Αναζήτηση</Label>
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="τραπέζι, χρήστης, περιγραφή…"
                className="pl-8"
                value={filterSearch}
                onChange={(e) => { setFilterSearch(e.target.value); setPage(0); }}
                data-testid="audit-filter-search"
              />
            </div>
          </div>
          <div>
            <Button
              variant="outline"
              className="w-full border-2"
              onClick={resetFilters}
              data-testid="audit-filter-reset"
            >
              <RotateCcw size={14} className="mr-1.5" />
              Καθαρισμός φίλτρων
            </Button>
          </div>
        </div>
        <div className="flex items-center justify-between text-xs text-slate-600 pt-1 border-t border-slate-100 gap-3 flex-wrap">
          <span>
            <strong data-testid="audit-total">{total}</strong> εγγραφ{total === 1 ? "ή" : "ές"} συνολικά
            {pageCount > 1 ? ` · σελίδα ${page + 1}/${pageCount}` : ""}
          </span>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 px-2 py-1 bg-slate-50 rounded border border-slate-200">
              <Label className="text-[11px] text-slate-600 font-bold">Retention:</Label>
              <Input
                type="number"
                min={0}
                max={3650}
                className="h-7 w-20 text-xs"
                value={retention}
                onChange={(e) => { setRetention(Number(e.target.value) || 0); setRetentionDirty(true); }}
                data-testid="audit-retention-days"
              />
              <span className="text-[11px] text-slate-500">ημέρες (0=πάντα)</span>
              {retentionDirty && (
                <Button
                  size="sm"
                  className="h-7 px-2 text-xs bg-blue-600 hover:bg-blue-700"
                  onClick={saveRetention}
                  data-testid="audit-retention-save"
                >
                  Αποθήκευση
                </Button>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0 || busy}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              data-testid="audit-page-prev"
            >
              <ChevronLeft size={14} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page + 1 >= pageCount || busy}
              onClick={() => setPage((p) => p + 1)}
              data-testid="audit-page-next"
            >
              <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      </div>

      {/* Feed list ---------------------------------------------------- */}
      {busy && items.length === 0 ? (
        <div className="text-center text-sm text-slate-500 py-12">Φόρτωση…</div>
      ) : items.length === 0 ? (
        <div className="text-center text-sm text-slate-500 py-12 border-2 border-dashed border-slate-200 rounded-lg bg-white">
          <ShieldAlert size={28} className="mx-auto mb-2 text-slate-400" />
          Δεν βρέθηκαν audit events με τα τρέχοντα φίλτρα.
        </div>
      ) : (
        <div className="space-y-2" data-testid="audit-feed-list">
          {items.map((it) => {
            const meta = ACTION_META[it.action] || { Icon: ShieldAlert, tone: "bg-slate-100 text-slate-700 border-slate-300" };
            const ActionIcon = meta.Icon;
            const sevClass = SEVERITY_BADGE[it.severity] || SEVERITY_BADGE.info;
            return (
              <div
                key={it.id}
                className={`border-2 rounded-lg p-3 flex items-start gap-3 ${meta.tone} bg-opacity-30`}
                data-testid={`audit-row-${it.id}`}
              >
                <div className="w-10 h-10 rounded-md grid place-items-center shrink-0 bg-white border-2 border-current/20">
                  <ActionIcon size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] border uppercase font-mono ${sevClass}`}>
                      {it.severity}
                    </span>
                    <span className="font-bold text-sm text-slate-900">{it.summary}</span>
                  </div>
                  <div className="text-xs text-slate-600 mt-1 flex items-center gap-3 flex-wrap">
                    <span className="font-mono">{fmtTs(it.timestamp)}</span>
                    <span>· {it.user_name || "—"}</span>
                    {it.target_name && <span>· {it.target_name}</span>}
                  </div>
                  {it.details && Object.keys(it.details).length > 0 && (
                    <details className="text-[11px] text-slate-500 mt-1.5">
                      <summary className="cursor-pointer hover:text-slate-700">Λεπτομέρειες</summary>
                      <pre className="bg-white/60 border border-slate-200 rounded p-2 mt-1 text-[10px] overflow-x-auto">
                        {JSON.stringify(it.details, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
